import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';

import { Table } from '../../../shared/table/table';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { TableColumn, TableAction } from '../../../shared/models/table-models';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { PKL_GRP, DropdownOption } from '../../../../core/shared/models/picklist.model';
import { AppIcons } from '../../../../core/icons/fa-icons';
import { BackButton } from '../../../shared/buttons/back-button';
import { Setup } from '../../shared/service/setup';

@Component({
  selector: 'app-relational-officer',
  standalone: true,
  imports: [TranslatePipe, CommonModule, Table, Popup, BackButton],
  templateUrl: './relational-officer.html',
  styleUrl: './relational-officer.css',
})
export class RelationalOfficer implements OnInit {
  icons = AppIcons;

  teamLeadSeq!: number;

  list: any[] = [];
  columns: TableColumn[] = [];

  showPopup = false;
  isEditMode = false;
  formData: any = {};

  fields: DynamicFieldConfig[] = [];

  private employees: any[] = [];
  private employeeOptions: DropdownOption[] = [];

  // ================= EMPLOYEE ASSIGNMENT =================
  showAssignPopup = false;
  assignFields: DynamicFieldConfig[] = [];
  assignFormData: any = {};
  private selectedRelOfficerSeq: number | null = null;
  private selectedEmpSeq: number | null = null;
  private selectedDescription: string | null = null;
  private selectedStatus: number | null = null;

  // ================= DELETE =================
  showDeletePopup = false;
  deleteMessage = '';
  private selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private toastr: ToastrService,
    private picklist: PickListService,
    private route: ActivatedRoute,
    private setup: Setup,
  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.teamLeadSeq = Number(params.get('id'));
      this.loadPicklists();
    });
  }

  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
    },
    {
      icon: this.icons.assignEmployee,
      tooltip: 'SETUP.EMPLOYEE_ASSIGNMENT',
      class: 'action-view',
      onClick: (row) => this.openAssignPopup(row),
    },
  ];

  // ================= PICKLIST =================
  loadPicklists() {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.loadEmployees();
      this.buildColumns();
      this.loadData();
    });
  }

  // ================= EMPLOYEES =================
  loadEmployees() {
    this.setup.getAllEmployees().subscribe({
      next: (res: any) => {
        this.employees = Array.isArray(res) ? res : res?.data || [];

        this.employeeOptions = this.employees.map((item: any) => ({
          label: item.empNm,
          value: item.empSeq,
        }));

        this.buildFields(this.employeeOptions);
      },
      error: () => {
        this.employees = [];
        this.employeeOptions = [];
        this.buildFields([]);
      },
    });
  }

  // ================= FORM =================
  buildFields(employeeOptions: DropdownOption[] = []) {
    this.fields = [
      { key: 'relOfficerSeq', label: 'SETUP.ID', type: 'text', hidden: true },
      {
        key: 'empSeq',
        label: 'SETUP.EMPLOYEE_NAME',
        type: 'select',
        options$: employeeOptions,
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: { required: true },
      },
      { key: 'description', label: 'COMMON.DESCRIPTION', type: 'textarea' },
    ];
  }

  // ================= TABLE =================
  buildColumns() {
    this.columns = [
      { key: 'relOfficerSeq', label: 'SETUP.ID' },

      { key: 'name', label: 'COMMON.NAME' },

      { key: 'description', label: 'COMMON.DESCRIPTION' },

      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: { 706: 'COMMON.ACTIVE', 707: 'COMMON.INACTIVE' },
        badgeMap: { 706: 'status-active', 707: 'status-inactive' },
      },
    ];
  }

  // ================= LOAD DATA =================
  loadData() {
    this.setup.getRelationalOfficers(this.teamLeadSeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];

        this.list = data.map((item: any) => ({
          relOfficerSeq: item.orgRoSeq,
          empSeq: item.empSeq,
          name: item.roNm,
          description: item.dscr,
          status: item.sts ? 706 : 707,
          cvrngEmpId: item.cvrngEmpId,
          cvrngEmpFrmDt: item.cvrngEmpFrmDt,
          cvrngEmpToDt: item.cvrngEmpToDt,
        }));
      },
      error: () => {
        this.list = [];
      },
    });
  }

  // ================= POPUP =================
  openPopup(data?: any) {
    this.showPopup = true;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        relOfficerSeq: data.relOfficerSeq,
        empSeq: data.empSeq,
        description: data.description,
        status: data.status,
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        relOfficerSeq: null,
        status: this.getDefaultStatus(),
      };
    }
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || null;
  }

  // ================= SAVE =================
  private buildRelOfficerPayload(
    orgRoSeq: number | null,
    empSeq: number,
    delFlg: number,
    description: string | null,
    status: number | null,
  ) {
    const employee = this.employees.find((emp: any) => emp.empSeq === empSeq);

    return {
      orgRoSeq,
      orgTlSeq: this.teamLeadSeq,
      empSeq,
      roNm: employee?.empNm ?? '',
      dscr: description ?? '',
      sts: status === 706,
      delFlg,
      cvrngEmpId: null,
      cvrngEmpFrmDt: null,
      cvrngEmpToDt: null,
    };
  }

  handleSave = (form: any) => {
    const payload = this.buildRelOfficerPayload(
      this.isEditMode ? form.relOfficerSeq : null,
      form.empSeq,
      0,
      form.description,
      form.status,
    );

    this.setup.saveRelationalOfficer(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant(this.isEditMode ? 'SETUP.ORG.RELATIONAL_OFFICER_UPDATED' : 'SETUP.ORG.RELATIONAL_OFFICER_CREATED'));
        this.showPopup = false;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED')),
    });
  };

  // ================= EMPLOYEE ASSIGNMENT =================
  buildAssignFields() {
    this.assignFields = [
      {
        key: 'empSeq',
        label: 'SETUP.ORG.RELATIONAL_OFFICER',
        type: 'select',
        options$: this.employeeOptions,
        validations: { required: true },
        disabled: true,
      },
      {
        key: 'coveringEmployee',
        label: 'SETUP.COVERING_EMPLOYEE',
        type: 'select',
        options$: this.employeeOptions,
        clearable: true,
      },
      {
        key: 'fromDate',
        label: 'SETUP.COVERING_FROM_DATE',
        type: 'date',
      },
      {
        key: 'toDate',
        label: 'SETUP.COVERING_TO_DATE',
        type: 'date',
      },
    ];
  }

  openAssignPopup(row: any) {
    this.selectedRelOfficerSeq = row.relOfficerSeq;
    this.selectedEmpSeq = row.empSeq;
    this.selectedDescription = row.description;
    this.selectedStatus = row.status;

    this.buildAssignFields();

    this.assignFormData = {
      empSeq: row.empSeq,
      coveringEmployee: row.cvrngEmpId || null,
      fromDate: row.cvrngEmpFrmDt,
      toDate: row.cvrngEmpToDt,
    };

    this.showAssignPopup = true;
  }

  handleAssignSave = (form: any) => {
    const payload = {
      ...this.buildRelOfficerPayload(
        this.selectedRelOfficerSeq,
        this.selectedEmpSeq as number,
        0,
        this.selectedDescription,
        this.selectedStatus,
      ),
      cvrngEmpId: form.coveringEmployee,
      cvrngEmpFrmDt: form.fromDate,
      cvrngEmpToDt: form.toDate,
    };

    this.setup.saveRelationalOfficer(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('SETUP.EMPLOYEE_ASSIGNED_SUCCESSFULLY'));
        this.showAssignPopup = false;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED')),
    });
  };

  // ================= DELETE =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.name });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;

    const payload = this.buildRelOfficerPayload(
      this.selectedRow.relOfficerSeq,
      this.selectedRow.empSeq,
      1,
      this.selectedRow.description,
      this.selectedRow.status,
    );

    this.setup.saveRelationalOfficer(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('SETUP.DELETED_SUCCESSFULLY'));
        this.showDeletePopup = false;
        this.selectedRow = null;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.DELETE_FAILED')),
    });
  }
}
